import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../common/firebase';
import SceneColorPicker from './SceneColorPicker';
import { defaultZoneName } from '../zoneUtils';

function colorsMatch(a = [], b = []) {
	if (a.length !== b.length) return false;
	return a.every((c, i) => c.r === b[i].r && c.g === b[i].g && c.b === b[i].b);
}

function getDisplayColors(currentSystem) {
	const colorArray = currentSystem?.color || [];
	const colorNames = currentSystem?.color_names || [];
	let numToRender = colorArray.length;
	if (typeof currentSystem?.num_colors === 'number' && currentSystem.num_colors > 0) {
		numToRender = currentSystem.num_colors;
	} else if (colorNames.length > 0) {
		numToRender = colorNames.length;
	}
	numToRender = Math.min(numToRender, colorArray.length);
	return {
		displayColors: colorArray.slice(0, numToRender),
		colorNames,
		numToRender,
	};
}

function CurrentOutputPanel({ currentSystem }) {
	const { displayColors, colorNames, numToRender } = getDisplayColors(currentSystem);
	const [liveColors, setLiveColors] = useState(() => displayColors.map((c) => ({ ...c })));
	const debounceRef = useRef(null);
	const latestRgbRef = useRef({});

	// Sync from Firestore only when remote content actually changes — not on every
	// new array reference from getDisplayColors (that was snapping picks back).
	const remoteKey = useMemo(
		() => displayColors.map((c) => `${c.r},${c.g},${c.b}`).join('|'),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional content key
		[currentSystem?.color, currentSystem?.num_colors, numToRender]
	);

	useEffect(() => {
		setLiveColors((prev) => {
			if (colorsMatch(prev, displayColors)) return prev;
			return displayColors.map((c) => ({ r: c.r, g: c.g, b: c.b }));
		});
	}, [remoteKey]); // eslint-disable-line react-hooks/exhaustive-deps -- displayColors paired with remoteKey

	const outputPalette = useMemo(() => {
		const seen = new Set();
		return liveColors.filter((c) => {
			const key = `${c.r},${c.g},${c.b}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [liveColors]);

	useEffect(() => () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
	}, []);

	const updateZoneColor = useCallback((zoneIndex, rgb) => {
		if (!currentSystem?.uid) return;
		latestRgbRef.current[zoneIndex] = { r: rgb.r, g: rgb.g, b: rgb.b };
		setLiveColors((prev) => {
			const next = [...prev];
			next[zoneIndex] = { r: rgb.r, g: rgb.g, b: rgb.b };
			return next;
		});
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			const colors = [...(currentSystem.color || [])];
			while (colors.length < numToRender) {
				colors.push({ r: 128, g: 128, b: 128 });
			}
			Object.entries(latestRgbRef.current).forEach(([idx, value]) => {
				colors[Number(idx)] = { ...value };
			});
			await updateDoc(doc(firestore, 'systems', currentSystem.uid), {
				color: colors.slice(0, Math.max(numToRender, colors.length)),
			});
		}, 120);
	}, [currentSystem, numToRender]);

	if (displayColors.length === 0) return null;

	return (
		<div className="mb-6 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl">
			<div className="flex items-baseline justify-between mb-3">
				<div>
					<div className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Current Output</div>
					<div className="font-medium">{currentSystem.current_scen_name || 'Custom'}</div>
				</div>
			</div>
			<p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">Tap a zone swatch to adjust live output.</p>
			<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
				{liveColors.map((c, i) => (
					<div key={i} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
						<SceneColorPicker
							color={c}
							onChange={(rgb) => updateZoneColor(i, rgb)}
							label={colorNames[i] || defaultZoneName(i)}
							size={28}
							sceneColors={outputPalette}
							zIndex={90}
						/>
						<div className="min-w-0 flex-1">
							<div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
								{colorNames[i] || defaultZoneName(i)}
							</div>
							<div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
								{c.r}, {c.g}, {c.b}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

export default CurrentOutputPanel;
export { getDisplayColors };
